import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PhotoImg, authedFileUrl, formatPrice } from "@/components/propertyDetail/helpers";
import {
  Eye, House, MapPin, Stack, Bed, Bathtub, Lightning, ArrowSquareOut,
  ImageSquare, Calendar, Sparkle, ArrowsLeftRight,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DismissibleIntro from "@/components/DismissibleIntro";

/**
 * AnteprimaTab — preview "scheda annuncio" come apparirà al cliente finale.
 * Iter 264: richiesta utente — "vedere l'annuncio finito da qualche parte
 * prima di pubblicarlo". Soluzione: nuovo sub-tab Annuncio/Anteprima che
 * mostra il preview con stessa estetica della /listing/{id} pubblica.
 *
 * Mostra:
 *  - Hero: foto cover (la prima enhanced o originale)
 *  - Header: titolo, prezzo, città/zona
 *  - Specs: tipo, mq, locali, bagni, piano, classe energetica
 *  - Galleria: tutte le foto (max 8)
 *  - Descrizione: testo annuncio (multi-colonna su desktop)
 *  - CTA: "Apri pagina online" se listing attivo, oppure suggerimento setup
 */
export default function AnteprimaTab() {
  const { property, realPhotos, listing, setTab, setProperty, id } = usePropertyDetail();

  // iter v28 r28 — Toggle "Mostra sezione Prima/Dopo" sulla pagina online.
  const [savingBA, setSavingBA] = useState(false);
  const showBA = property?.show_before_after_section !== false; // default true
  const hasBeforeAfters = realPhotos.some((p) => p.staged_path && p.original_path);
  const toggleBA = async () => {
    if (savingBA) return;
    setSavingBA(true);
    const next = !showBA;
    try {
      const { data } = await api.put(`/properties/${id}`, { show_before_after_section: next });
      setProperty((prev) => ({ ...prev, ...data, show_before_after_section: next }));
      toast.success(next ? "Sezione Prima/Dopo attivata" : "Sezione Prima/Dopo nascosta");
    } catch (e) {
      toast.error("Impossibile salvare la preferenza");
    } finally {
      setSavingBA(false);
    }
  };

  if (!property?.ai_description) {
    return (
      <div className="mt-8 max-w-2xl mx-auto text-center py-12" data-testid="anteprima-empty">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F1F4ED] mb-4">
          <Eye size={28} weight="light" className="text-[#7C8B6E]" />
        </div>
        <h3 className="font-editorial text-2xl mb-2">Genera prima il testo</h3>
        <p className="text-sm text-[#6B6A65] mb-5 leading-relaxed">
          Per vedere l'anteprima dell'annuncio finito devi prima creare il testo nella scheda "Testo".
        </p>
        <button
          onClick={() => setTab("descrizione")}
          className="inline-flex items-center gap-2 bg-[#1C1C1A] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-black"
          data-testid="anteprima-go-testo"
        >
          <Sparkle size={14} weight="fill" /> Vai a "Testo"
        </button>
      </div>
    );
  }

  const cover = realPhotos[0];
  const coverUrl = cover ? authedFileUrl(cover.enhanced_path || cover.original_path) : null;
  const galleryPhotos = realPhotos.slice(1, 9);
  const remainingCount = Math.max(0, realPhotos.length - 9);

  return (
    <div className="mt-6 max-w-5xl mx-auto" data-testid="anteprima-tab">
      {/* Banner top: spiega cos'è e link a pagina online se attiva.
          iter v28 r177 — dismissibile: una volta letto, l'agente lo chiude.
          Il link "Apri pagina pubblica" resta visibile in caso di chiusura
          perché lo spostiamo subito sotto (vedi `anteprima-quick-link`). */}
      <DismissibleIntro storageKey="anteprima:intro" align="left" className="mb-5">
        <div className="flex items-center justify-between gap-3 flex-wrap p-3 pr-9 rounded-lg bg-[#F1F4ED] border border-[#E5E7E0]">
          <div className="flex items-start gap-2 min-w-0">
            <Eye size={16} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A]">
                Anteprima dell'annuncio finito
              </div>
              <div className="text-[11px] text-[#6B6A65] leading-snug">
                Così apparirà ai clienti quando attivi la pagina online.
              </div>
            </div>
          </div>
        </div>
      </DismissibleIntro>
      {listing?.public_id && (
        <div className="mb-5 flex justify-end" data-testid="anteprima-quick-link">
          <a
            href={`/listing/${listing.public_id}`}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[12px] font-semibold text-[#1C1C1A] transition-colors"
            data-testid="anteprima-open-public"
          >
            <ArrowSquareOut size={12} weight="bold" /> Apri pagina pubblica
          </a>
        </div>
      )}

      {/* iter v28 r42 — Stile pagina online + Colore brand SPOSTATI in
          tab "Pubblica" (CondivisioneTab). L'agente li sceglie lì,
          dove pubblica davvero. Qui resta solo l'anteprima del finito. */}

      {/* iter v28 r29 — Toggle "Mostra Prima · Dopo" UNIFICATO.
          Controlla TUTTO: galleria con sequenza, slider interattivo,
          export portali. Nascosto se non ci sono foto Prima/Dopo. */}
      {hasBeforeAfters && (
        <div className="mb-5 p-3.5 rounded-lg bg-white border border-[#E5E7E0]" data-testid="public-display-options">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2.5">
            Cosa mostrare al pubblico
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={showBA}
                onChange={toggleBA}
                disabled={savingBA}
                className="sr-only peer"
                data-testid="toggle-show-before-after"
              />
              <div className="w-10 h-6 bg-[#E5E7E0] rounded-full transition-colors peer-checked:bg-[#7C8B6E] peer-disabled:opacity-50" />
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <ArrowsLeftRight size={14} weight="bold" className="text-[#7C8B6E] flex-shrink-0" />
                <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight">
                  Mostra Prima · Dopo
                </div>
              </div>
              <div className="text-[11.5px] text-[#6B6A65] mt-1 leading-snug">
                {showBA ? (
                  <>
                    Pagina online e portali mostrano le coppie <strong>Prima · Dopo</strong> in sequenza.
                    Sulla pagina online appare anche lo slider interattivo trascinabile. La copertina
                    resta sempre con la foto migliorata.
                  </>
                ) : (
                  <>
                    Pagina online e portali mostrano <strong>solo le foto modificate</strong>.
                    Nessun confronto, niente foto originali pubbliche.
                  </>
                )}
              </div>
            </div>
          </label>
        </div>
      )}

      {/* Card mock-up — stesso stile della pagina pubblica */}
      <div className="rounded-2xl overflow-hidden bg-white border border-[#E5E7E0] shadow-sm">
        {/* Hero cover */}
        <div className="relative aspect-[16/9] bg-[#F1EEE6] overflow-hidden" data-testid="anteprima-cover">
          {coverUrl ? (
            <PhotoImg
              src={coverUrl}
              alt=""
              className="w-full h-full object-cover"
              fallbackLabel="Manca la cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-[#9A968B]">
              <div className="text-center">
                <ImageSquare size={36} weight="light" className="text-[#C8C4B7] mx-auto mb-2" />
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold">Carica una foto cover</div>
              </div>
            </div>
          )}
          {/* Floating badge tipologia */}
          <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full text-[#1C1C1A]">
            {property.property_type || "Immobile"}{property.listing_type === "affitto" ? " · affitto" : ""}
          </span>
        </div>

        {/* Header */}
        <div className="px-5 sm:px-8 pt-6 pb-4">
          <h1 className="font-editorial text-2xl sm:text-3xl text-[#1C1C1A] leading-tight" data-testid="anteprima-title">
            {property.title || "Senza titolo"}
          </h1>
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mt-2">
            <span className="text-2xl font-semibold text-[#1C1C1A] tabular-nums" data-testid="anteprima-price">
              {formatPrice(property.price)}{property.listing_type === "affitto" ? <span className="text-sm text-[#6B6A65]"> /mese</span> : null}
            </span>
            {(property.city || property.neighborhood) && (
              <span className="text-[13px] text-[#6B6A65] inline-flex items-center gap-1">
                <MapPin size={12} weight="fill" className="text-[#7C8B6E]" />
                {[property.neighborhood, property.city].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
        </div>

        {/* Specs strip */}
        <div className="px-5 sm:px-8 py-3 border-y border-[#E5E7E0] bg-[#FAFAF7]">
          <div className="flex items-center gap-5 sm:gap-7 flex-wrap text-[12.5px] text-[#3F3D38]" data-testid="anteprima-specs">
            {property.surface && (
              <span className="inline-flex items-center gap-1.5"><Stack size={14} weight="bold" className="text-[#7C8B6E]" /> <strong>{property.surface}</strong> m²</span>
            )}
            {property.rooms && (
              <span className="inline-flex items-center gap-1.5"><House size={14} weight="bold" className="text-[#7C8B6E]" /> <strong>{property.rooms}</strong> locali</span>
            )}
            {property.bathrooms && (
              <span className="inline-flex items-center gap-1.5"><Bathtub size={14} weight="bold" className="text-[#7C8B6E]" /> <strong>{property.bathrooms}</strong> bagni</span>
            )}
            {property.floor && (
              <span className="inline-flex items-center gap-1.5"><Bed size={14} weight="bold" className="text-[#7C8B6E]" /> <strong>{property.floor}</strong> piano</span>
            )}
            {property.energy_class && (
              <span className="inline-flex items-center gap-1.5"><Lightning size={14} weight="bold" className="text-[#7C8B6E]" /> classe <strong>{property.energy_class}</strong></span>
            )}
            {property.year_built && (
              <span className="inline-flex items-center gap-1.5"><Calendar size={14} weight="bold" className="text-[#7C8B6E]" /> anno <strong>{property.year_built}</strong></span>
            )}
          </div>
        </div>

        {/* Body: descrizione (a 2 colonne su md+) + gallery */}
        <div className="px-5 sm:px-8 py-7 grid grid-cols-1 lg:grid-cols-5 gap-7">
          {/* Descrizione — 3/5 della larghezza, multi-colonna interna */}
          <div className="lg:col-span-3" data-testid="anteprima-description">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-2">
              Descrizione
            </div>
            <div
              className="text-[14px] text-[#1C1C1A] leading-[1.65] whitespace-pre-line"
              style={{ columnCount: 1, columnGap: "2rem" }}
            >
              {property.ai_description}
            </div>
          </div>

          {/* Gallery mini grid 2/5 */}
          <div className="lg:col-span-2" data-testid="anteprima-gallery">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-2">
              Galleria · {realPhotos.length} foto
            </div>
            {galleryPhotos.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {galleryPhotos.map((p) => (
                  <div key={p.id} className="aspect-square rounded-md overflow-hidden bg-[#F1EEE6]">
                    <PhotoImg
                      src={authedFileUrl(p.enhanced_path || p.original_path)}
                      alt=""
                      className="w-full h-full object-cover"
                      fallbackLabel=""
                    />
                  </div>
                ))}
                {remainingCount > 0 && (
                  <div className="aspect-square rounded-md flex items-center justify-center bg-[#1C1C1A] text-white text-sm font-bold col-span-2">
                    +{remainingCount} altre foto
                  </div>
                )}
              </div>
            ) : (
              <div className="text-[11.5px] text-[#9A968B] italic">
                Carica più foto per popolare la galleria.
              </div>
            )}
          </div>
        </div>

        {/* Footer · iter v28 r20 — Rimosso bottone «Attiva la pagina online»
            per richiesta utente: la pubblicazione si fa dal tab "Condivisione"
            dedicato, no duplicazione di CTA che confonde. Resto solo con
            chip-stato discreto sulla destra. */}
        <div className="px-5 sm:px-8 pb-7 pt-3 border-t border-[#E5E7E0] bg-[#FAFAF7] flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11px] text-[#9A968B]">
            Anteprima · le richieste di sopralluogo arriveranno qui sotto.
          </div>
          {!listing?.exists ? (
            <span className="text-[11px] text-[#9A968B]" data-testid="anteprima-not-public">
              Non ancora online · vai in <button onClick={() => setTab("condivisione")} className="underline hover:text-[#1C1C1A] font-medium" data-testid="anteprima-go-condivisione">Condivisione</button> per pubblicare.
            </span>
          ) : !listing.is_public ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-900 text-[11.5px] font-medium" data-testid="anteprima-offline">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Pagina offline
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7C8B6E]/15 text-[#7C8B6E] text-[11.5px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E]"></span> Pagina online attiva
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
